import React, { useCallback, useEffect, useMemo, useState } from "react";
import Map, { Marker, Source, Layer, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { toast } from "sonner";
import { Route as RouteIcon, RefreshCw, MapPin, Clock, Navigation, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_CENTER, VISIT_STATUS_COLORS } from "@/pages/fleet/constants";
import { useMapboxToken } from "@/hooks/useMapboxToken";

const todayRiyadh = () => {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  return p; // YYYY-MM-DD
};
const shiftDay = (iso, delta) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};
const fmtDayLabel = (iso) => {
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
    return iso === todayRiyadh() ? `Today · ${label}` : label;
  } catch { return iso; }
};

export default function MyRoute() {
  const MAPBOX_TOKEN = useMapboxToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(todayRiyadh);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/fleet/my-route", { params: { day } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  const isToday = day === todayRiyadh();
  const stops = data?.stops || [];
  const center = useMemo(() => {
    if (data?.start) return { longitude: data.start.longitude, latitude: data.start.latitude, zoom: 11 };
    if (stops[0]) return { longitude: stops[0].longitude, latitude: stops[0].latitude, zoom: 11 };
    return { longitude: DEFAULT_CENTER.longitude, latitude: DEFAULT_CENTER.latitude, zoom: 10 };
  }, [data, stops]);

  return (
    <div data-testid="my-route">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937] flex items-center gap-2">
            <RouteIcon className="w-6 h-6 text-[#0B7E8C]" /> My Route
          </h1>
          <p className="text-[13px] text-[#6B7280] mt-1">Driving order for your assigned visits, in appointment-time order</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Day navigator */}
          <div className="inline-flex items-center rounded-lg border border-[#E5E7EB] bg-white overflow-hidden" data-testid="my-route-day-nav">
            <button onClick={() => setDay((d) => shiftDay(d, -1))} data-testid="my-route-prev-day"
                    className="px-2 py-2 hover:bg-[#F8FAFC] text-[#374151]" aria-label="Previous day">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <label className="relative flex items-center gap-2 px-3 py-2 border-x border-[#E5E7EB] cursor-pointer">
              <CalendarDays className="w-4 h-4 text-[#0B7E8C]" />
              <span className="text-[13px] font-medium text-[#1F2937] whitespace-nowrap" data-testid="my-route-day-label">{fmtDayLabel(day)}</span>
              <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)}
                     data-testid="my-route-day-input"
                     className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
            </label>
            <button onClick={() => setDay((d) => shiftDay(d, 1))} data-testid="my-route-next-day"
                    className="px-2 py-2 hover:bg-[#F8FAFC] text-[#374151]" aria-label="Next day">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {!isToday && (
            <button onClick={() => setDay(todayRiyadh())} data-testid="my-route-today"
                    className="px-3 py-2 rounded-lg border border-[#E5E7EB] text-[13px] text-[#374151] hover:bg-[#F8FAFC]">
              Today
            </button>
          )}
          <button onClick={load} data-testid="my-route-refresh"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E7EB] text-[13px] text-[#374151] hover:bg-[#F8FAFC]">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Optimizing route…</div>
      ) : stops.length === 0 ? (
        <div className="hah-card p-10 text-center" data-testid="my-route-empty">
          <MapPin className="w-8 h-8 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">{isToday ? "No located visits assigned to you today." : `No located visits scheduled for ${fmtDayLabel(day)}.`}</p>
          {data?.unlocated_count > 0 && (
            <p className="text-[12px] text-[#F59E0B] mt-2">{data.unlocated_count} assigned visit(s) have no map location.</p>
          )}
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <SummaryTile icon={MapPin} label="Stops" value={stops.length} />
            <SummaryTile icon={Navigation} label="Total Distance" value={`${data.distance_km} km`} />
            <SummaryTile icon={Clock} label="Est. Drive Time" value={`${Math.round(data.duration_min)} min`} />
          </div>

          {/* Map */}
          <div className="hah-card p-3 mb-4">
            <div className="relative w-full h-[440px] rounded-xl overflow-hidden border border-[#E5E7EB]">
              {MAPBOX_TOKEN ? (
                <Map mapboxAccessToken={MAPBOX_TOKEN} initialViewState={center}
                     mapStyle="mapbox://styles/mapbox/light-v11" attributionControl={false}
                     style={{ width: "100%", height: "100%" }}>
                  <NavigationControl position="bottom-right" showCompass={false} />
                  {data.geometry && (
                    <Source id="route" type="geojson" data={{ type: "Feature", geometry: data.geometry }}>
                      <Layer id="route-line" type="line"
                             layout={{ "line-cap": "round", "line-join": "round" }}
                             paint={{ "line-color": "#0B7E8C", "line-width": 4, "line-opacity": 0.8 }} />
                    </Source>
                  )}
                  {data.start && (
                    <Marker longitude={data.start.longitude} latitude={data.start.latitude} anchor="center">
                      <span className="w-6 h-6 rounded-full bg-[#1F2937] ring-2 ring-white shadow flex items-center justify-center">
                        <Navigation className="w-3 h-3 text-white" />
                      </span>
                    </Marker>
                  )}
                  {stops.map((s) => (
                    <Marker key={s.visit_id} longitude={s.longitude} latitude={s.latitude} anchor="center">
                      <span className="w-7 h-7 rounded-full bg-[#0B7E8C] text-white text-[12px] font-bold ring-2 ring-white shadow flex items-center justify-center">
                        {s.stop_number}
                      </span>
                    </Marker>
                  ))}
                </Map>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-[#94A3B8]">Map unavailable.</div>
              )}
            </div>
          </div>

          {/* Ordered list */}
          <div className="hah-card p-5" data-testid="my-route-list">
            <h2 className="text-[15px] font-semibold text-[#1F2937] mb-3">Visit Order</h2>
            <div className="space-y-2">
              {stops.map((s) => {
                const c = VISIT_STATUS_COLORS[s.status] || { text: "#64748B", label: s.status };
                return (
                  <div key={s.visit_id} data-testid={`my-route-stop-${s.stop_number}`}
                       className="flex items-center gap-3 p-3 rounded-lg border border-[#F1F5F9] hover:bg-[#F8FAFC]">
                    <span className="w-8 h-8 rounded-full bg-[#0B7E8C] text-white text-sm font-bold flex items-center justify-center shrink-0">
                      {s.stop_number}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-medium text-[#1F2937] truncate">{s.patient_name}</div>
                      <div className="text-[12px] text-[#6B7280] capitalize">
                        {s.scheduled_at ? `${fmtTime(s.scheduled_at)} · ` : ""}{(s.visit_type || "").replace("_", " ")}{s.city ? ` · ${s.city}` : ""}
                      </div>
                    </div>
                    <span className="text-[11px] px-2 py-0.5 rounded-md font-medium shrink-0"
                          style={{ background: `${c.text}14`, color: c.text }}>{c.label}</span>
                  </div>
                );
              })}
            </div>
            {data.unlocated_count > 0 && (
              <p className="text-[12px] text-[#F59E0B] mt-3">{data.unlocated_count} assigned visit(s) hidden — no map location.</p>
            )}
            {data.source === "fallback" && (
              <p className="text-[11px] text-[#94A3B8] mt-3">Route estimated from straight-line distances (live driving routes temporarily unavailable).</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh" });
  } catch {
    return "";
  }
}

function SummaryTile({ icon: Icon, label, value }) {
  return (
    <div className="hah-card p-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-[#0B7E8C1A] text-[#0B7E8C] flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <div className="text-[20px] font-bold text-[#1F2937] leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] mt-1">{label}</div>
      </div>
    </div>
  );
}
